import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { format, isAfter, isSameMonth } from "date-fns";
import { toast } from "react-toastify";
import { MuiTelInputInfo } from "mui-tel-input";

import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Rating,
  Tab,
  Tabs,
  type SelectChangeEvent,
  Typography,
  useTheme
} from "@mui/material";
import {
  AddCircleOutline,
  AttachMoney,
  People,
  Savings,
  Star,
  Stream,
  WorkHistory,
} from "@mui/icons-material";

import {
  CalendarIcon,
  CheckIcon,
  LocationIcon,
  StarIcon,
  WarningIcon,
} from "../../components/icons";
import { tokens } from "../../theme";
import { getStatusColors } from "../../constants/statusColors";
import apiService from "../../service/Api";
import {
  AddCrewMemberDialog,
  CrewStatistics,
  SearchPilotDialog,
} from "./components";
import { RootState } from "../../store/Index";

import {
  CrewStatisticsTypes,
  ICrewMember,
  IRawCrew,
} from "./types/crew-dashboard";
import {
  AddCrewFormProps,
  RoleSkillTool
} from "./types/add-crew-member";

import { activities, crewPayments } from "../../data/CrewData";
import { UserProps } from "./types/search-pilot";
import { IPilotReviewProps } from "./types/reviews";
import { PilotAPIResponse } from "./types/view-pilot";
import { AxiosError } from "axios";

interface MappedStream {
  id: number;
  bookingId: string;
  type: string;
  title: string;
  serviceDate: string;
  venue: string;
  start: string;
  end: string;
}

export interface StreamResponse {
  id: number;
  booking: {
    services?: Array<{
      venue: {
        name: string | null;
      };
    }>;
    id: number;
    status: string;
    booking_type: string;
    package_item: {
      name: string | null;
      price: string | null;
    };
    events?: Array<{
      id: number;
      start_time: string;
      end_time: string;
      venue: {
        id: number;
        name: string;
        address_one: string;
        address_two: string;
        suburb: string;
        city: string;
      };
      title: string;
      service_notes: string;
      special_request: string | null;
      start_date_of_event: string;
      end_date_of_event: string;
      access_type: string;
      streaming_platform: string;
      session_to_stream: string;
      retention_duration: string;
      provide_on_demand_access: boolean;
      record_stream: boolean;
      require_custom_branding: boolean;
      branding_requirements: string;
      expected_audience_size: number;
    }>;
  };
  service: {
    venue: {
      name: string
    };
    church_name: string;
    type_of_service: string | null;
  };
  event_details: {
    venue: {
      name: string | null;
    };
  };
  title: string;
  date: string;
  address_one: string;
  address_two: string;
  suburb: string;
  city: string;
  start: string;
  end: string;
  vimeo_download_password: string | null;
  vimeo_stream_link: string | null;
  over_due_by: string | null;
  password_protected: boolean | null;
}

const CrewManagement: React.FC = () => {
  const [selectedCountry, setSelectedCountry] = useState<string | null>();
  const [selectedRoles, setSelectedRoles] = useState<RoleSkillTool[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<RoleSkillTool[]>([]);
  const [selectedTools, setSelectedTools] = useState<RoleSkillTool[]>([]);
  const [searchMemberOpen, setSearchMemberOpen] = useState(false);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<UserProps | null>();
  const [topRatedCrew, setTopRatedCrew] = useState<IPilotReviewProps[]>([]);
  const [mostRecentFeedback, setMostRecentFeedback] =
    useState<IPilotReviewProps>();
  const [activeTab, setActiveTab] = useState(0);
  const [crew, setCrew] = useState<ICrewMember[]>([]);
  const [formData, setFormData] = useState<AddCrewFormProps>({
    existingUser: false,
    user_id: null,
    first_name: "",
    last_name: "",
    email: "",
    username: "",
    password: "tilapia123",
    phone: "",
    role: "crew",
    organization: "",
    street_address: "",
    city: "",
    state: "",
    postal_code: "",
    residence_country: "",
    emergency_contact: "",
    emergency_contact_phone: "",
    date_of_birth: "1970-01-01",
    occupation: "",
    marital_status: 'single',
    national_id: "",
    roles: [],
    skills: [],
    tools: [],
    preferences: "",
    experience_years: 0
  });
  const [crewStats, setCrewStats] = useState<CrewStatisticsTypes>({
    total: 0,
    streams: 0,
    pendingRequests: 0,
    pendingPayouts: 0,
  });
  const [streams, setStreams] = useState<MappedStream[]>([]);
  const [crewLoading, setCrewLoading] = useState(false);
  const [crewError, setCrewError] = useState<string | null>(null);
  const [crewWarning, setCrewWarning] = useState<string | null>(null);
  const [streamsWarning, setStreamsWarning] = useState<string | null>(null);
  const [streamsLoading, setStreamsLoading] = useState(false);
  const [streamsError, setStreamsError] = useState<string | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  const navigate = useNavigate();

  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);
  const statusColors = getStatusColors(theme.palette.mode);
  const primaryColor = colors.primary[400];
  const primaryHoverColor = colors.primary[600];

  const fetchCrewData = useCallback(async () => {
    setCrewLoading(true);
    setCrewError(null);
    setCrewWarning(null);

    try {
      const response = await apiService.get<{
        count: number;
        results: IRawCrew[];
        next: string | null;
        previous: string | null;
      }>("/api/crews/crew-members/");

      const crewRatings = await apiService.get<{
        count: number;
        results: IPilotReviewProps[];
        next: string | null;
        previous: string | null;
      }>("/api/crews/crew-ratings/");

      let sortedCrewReviews = [];
      const today = new Date();
      sortedCrewReviews = crewRatings.data.results
        .filter((rvw) => isSameMonth(rvw.created_at, today))
        .sort((a, b) => b.rating - a.rating);

        const mappedCrew = response.data.results.map(
          (crew: IRawCrew): ICrewMember => ({
            id: crew.id,
            name: crew.user.first_name,
            surname: crew.user.last_name,
            email: crew.user.email,
            phone: crew.user?.phone_number || crew.user.user_type === 'company_user' ?
              crew.user.company?.phone_number : crew.user.individual?.phone_number,
            role: crew.user.role,
            department: crew.roles_detail[0]?.name || "", // need to clearly define between role and department
            status: "Available", // need to have this field or logic - indulge HURT
            avatar: crew.user?.profile_picture || "",
            joinDate: format(new Date(crew.created_at), "dd MMM, yyyy"),
            totalEarnings: 3000, // need this logic & fields from the backend
            totalAssignments: 7, // also need this logic & fields from the backend
            averageRating: 4.5, // also need this logic & fields from the backend
            lastGig: "N/A",
            nextGig: "N/A",
            roles: crew.roles_detail || [],
            skills: crew.skills_detail || [], // this would ideally be an array of strings to map 
            tools: crew.tools_detail || [],
            emergencyContact: {
              name: "",
              phone: "",
            },
            address: "",
            documents: crew?.document_certifications || [],
          })
        );

        setMostRecentFeedback(sortedCrewReviews[sortedCrewReviews.length - 1]);
        setTopRatedCrew(sortedCrewReviews.slice(0, 3));
        setCrewStats((prev) => ({
          ...prev,
          total: response.data.count
        }));

        setCrew(mappedCrew);
        if (
          mappedCrew.some(
            (c) => (!c.name || !c.surname || !c.email))
        ) {
          setCrewWarning(
            "Some pilots have missing information, which may affect display or search."
          );
        }
      } catch (error) {
        if (error instanceof AxiosError) {
          const errorMessage = error.response?.data?.message || "Failed to load pilots. Please try again.";
          setCrewError(errorMessage);
        } else {
          console.log({ error }, 'fetchCrew error');
        }
      } finally {
        setCrewLoading(false);
      }
    },
  []);

  useEffect(() => {
    if (streams.length) {
      const today = new Date();
      let streamsThisMonth = [];

      streamsThisMonth = streams.filter((strm) =>
        isSameMonth(strm.serviceDate, today)
      );

      setCrewStats((prev) => ({
        ...prev,
        streams: streamsThisMonth.length,
      }));
    }
  }, [streams]);

  useEffect(() => {
    fetchCrewData();
  }, [fetchCrewData]);

  const fetchStreamsData = useCallback(async () => {
    setStreamsLoading(true);
    setStreamsError(null);
    setStreamsWarning(null);

    try {
      const response = await apiService.get<{
        count: number;
        next: string | null;
        previous: string | null;
        results: StreamResponse[];
      }>("/api/streams/stream/no-callsheet/");
      
      const mappedStreams = response.data.results.map(
        (stream: StreamResponse) => {
          return {
            id: stream.id,
            bookingId: `B${stream.booking.id.toString().padStart(3, "0")}`,
            type: stream.booking.booking_type,
            title: stream.title || stream?.service?.church_name || "Untitled",
            serviceDate: format(new Date(stream.date), "dd MMM yyyy"),
            venue: `${
              stream?.service?.venue?.name ||
              stream?.booking?.events?.[0]?.venue?.name ||
              stream?.booking?.services?.[0]?.venue?.name || ""
            }`,
            start: stream.start,
            end: stream.end,
          };
        }
      );

      setStreams(mappedStreams);
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage =
          error.response?.data?.message ||
          "Failed to load streams. Please try again.";
        setStreamsError(errorMessage);
      } else {
        console.log({ error }, "fetchStreams error");
      }
    } finally {
      setStreamsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStreamsData();
  }, [fetchStreamsData]);

  useEffect(() => {
    fetchCrewData();
  }, [addMemberOpen, fetchCrewData]);

  const handleInputChange = (field: string, value: string) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  // make these phone input handlers into one portable handler for dynamic fields
  const handleEmergencyContactChange = (
    value: string,
    info: MuiTelInputInfo
  ) => {
    console.log({ value });
    setFormData((prev: AddCrewFormProps) => ({
      ...prev,
      emergency_contact_phone: `+${info.countryCallingCode}${info.nationalNumber}`,
    }));
  };

  // make these phone input handlers into one portable handler for dynamic fields
  const handlePhoneChange = (value: string, info: MuiTelInputInfo) => {
    console.log({ value });
    setFormData((prev: AddCrewFormProps) => ({
      ...prev,
      phone: `+${info.countryCallingCode}${info.nationalNumber}`,
    }));
  };

  const removeRole = (roleToRemove: number) => {
    setSelectedRoles(selectedRoles.filter((role) => role.id !== roleToRemove));
  };

  const removeSkill = (skillToRemove: number) => {
    setSelectedSkills(selectedSkills.filter((skill) => skill.id !== skillToRemove));
  };

  const removeTool = (toolToRemove: number) => {
    setSelectedTools(selectedTools.filter((tool) => tool.id !== toolToRemove));
  };

  const handleCountryChange = (country: string | null) => {
    setSelectedCountry(country);

    setFormData((prev: AddCrewFormProps) => ({
      ...prev,
      residence_country: country
    }));
  };

  const handleRolesChange = (roles: RoleSkillTool[]) => {
    if (selectedRoles.find(role => role.id === roles[roles.length - 1].id)) {
      toast.error(`The role ${roles[roles.length - 1].name} has already been selected`);
      return;
    }
    setSelectedRoles([ ...selectedRoles, roles[roles.length - 1]]);

    // eslint-disable-next-line prefer-const
    let roleIds: number[] = [];
    roles.forEach((role: RoleSkillTool) => {
      roleIds.push(role.id);
    });

    setFormData((prev: AddCrewFormProps) => ({
      ...prev,
      role: roles[0].name,
      roles: roleIds,
    }));
  };

  const handleSkillsChange = (skills: RoleSkillTool[]) => {
    if (selectedSkills.find(skill => skill.id === skills[skills.length - 1].id)) {
      toast.error(`The skill ${skills[skills.length - 1].name} has already been selected`);
      return;
    }
    setSelectedSkills([ ...selectedSkills, skills[skills.length - 1]]);

    // eslint-disable-next-line prefer-const
    let skillIds: number[] = [];
    skills.forEach((skill: RoleSkillTool) => {
      skillIds.push(skill.id);
    });

    setFormData((prev: AddCrewFormProps) => (
      { ...prev, skills: skillIds }
    ));
  };

  const handleToolsChange = (tools: RoleSkillTool[]) => {
    if (selectedTools.find(tool => tool.id === tools[tools.length - 1].id)) {
      toast.error(`The tool ${tools[tools.length - 1].name} has already been selected`);
      return;
    }
    setSelectedTools([ ...selectedTools, tools[tools.length - 1]]);

    // eslint-disable-next-line prefer-const
    let toolIds: number[] = [];
    tools.forEach((tool: RoleSkillTool) => {
      toolIds.push(tool.id);
    });

    setFormData((prev: AddCrewFormProps) => (
      { ...prev, tools: toolIds }
    ));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name as string]: value,
    }));
  };

  const handleAddCrewMember = async () => {
    const {
      email,
      existingUser,
      date_of_birth,
      first_name,
      last_name,
      password,
      phone,
      roles,
      username
    } = formData;

    if (!first_name.length) {
      toast.error("Please provide a first name for this pilot.");
      return;
    }

    if (!last_name.length) {
      toast.error("Please provide a last name for this pilot.");
      return;
    }

    if (!email.length) {
      toast.error("Please provide an email for this pilot.");
      return;
    }

    if (!username.length) {
      toast.error("Please provide a username for this pilot.");
      return;
    }

    if (!phone) {
      toast.error("Please provide a phone number for this pilot.");
      return;
    }

    if (!roles.length) {
      toast.error("Please select at least 1 role for this pilot.");
      return;
    }

    if (!password.length) {
      toast.error("Please provide a password for this pilot.");
      return;
    }

    if (!date_of_birth) {
      toast.error("Please provide a date of birth for this pilot.");
      return;
    }

    let payload = {};
    if (existingUser) {
      payload = {
        user_id: formData.user_id,
        roles: formData.roles,
        skills: formData.skills,
        tools: formData.tools,        
        experience_years: formData.experience_years,
        bio: "Pixel Pilot"
      };
    } else {
      payload = {
        user: {
          email: email,
          username: username,
          first_name: first_name,
          last_name: last_name,
          password: password,
          user_type: 'individual',
          phone_number: formData.phone,
          role: 'crew',
          organization: formData.organization,
          street_address: formData.street_address,
          city: formData.city,
          state_province: formData.state,
          postal_code: formData.postal_code,
          residence_country: formData.residence_country,
          date_of_birth: formData.date_of_birth,
          occupation: formData.occupation,
          marital_status: formData.marital_status,
          national_id: formData.national_id,
          emergency_contact: formData.emergency_contact,
          emergency_contact_phone: formData.emergency_contact_phone,
          assignment: selectedRoles[0].name,
          service_preferences: formData.preferences,
        },
        roles: formData.roles,
        skills: formData.skills,
        tools: formData.tools,
        experience_years: formData.experience_years,
        bio: "Pixel Pilot"
      };
    }

    setFormLoading(true);

    try {
      await apiService.post("/api/crews/crew-members/full-create/", payload);

      toast.success("Crew member added successfully!");
      resetForm();
      setAddMemberOpen(false);

      fetchCrewData();
    } catch (error) {
      if (error instanceof AxiosError) {
        const detail = error?.response?.data?.errors?.user_id ? error?.response?.data?.errors?.user_id[0] :
          error?.response?.data?.detail || "Failed to add crew member.";
        toast.error(detail);
      } else {
        console.error("Add crew error:", error);
      }
    } finally {
      setFormLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      existingUser: false,
      user_id: null,
      first_name: "",
      last_name: "",
      email: "",
      username: "",
      password: "tilapia123",
      phone: "",
      role: "crew",
      organization: "",
      street_address: "",
      city: "",
      state: "",
      postal_code: "",
      residence_country: "",
      emergency_contact: "",
      emergency_contact_phone: "",
      date_of_birth: "",
      occupation: "",
      marital_status: 'single',
      national_id: "",
      roles: [],
      skills: [],
      tools: [],
      preferences: "",
      experience_years: 0
    });
  };

  const getCrewActivityIcon = (type: string) => {
    switch (type) {
      case "checkedin":
        return <CheckIcon sx={{ fontSize: 24 }} color="info" />;
      case "notcheckedin":
        return <WarningIcon sx={{ fontSize: 24 }} color="warning" />;
      case "completed":
        return <CheckIcon sx={{ fontSize: 24 }} color="success" />;
      case "rating":
        return <StarIcon sx={{ fontSize: 24 }} color="warning" />;
      default:
        return <CheckIcon sx={{ fontSize: 24 }} color="info" />;
    }
  };

  const getAssignmentPriorityChipProps = (priority: string) => {
    switch (priority.toLowerCase()) {
      case "high":
        return {
          label: "High",
          bgcolor: "#EB433540",
          color: "#EB4335",
        };
      case "low":
        return {
          label: "Low",
          bgcolor: statusColors.fulfilled.bg,
          color: statusColors.fulfilled.fg,
        };
      default:
        return {
          label: "Unknown",
          bgcolor: statusColors.pending.bg,
          color: statusColors.pending.fg,
        };
    }
  };

  const getCrewAvailabilityChipProps = (status: string) => {
    switch (status.toLowerCase()) {
      case "available":
        return {
          label: "Available",
          bgcolor: "#4BA66340",
          color: "#4BA663",
        };
      case "busy":
      case "assigned":
        return {
          label: "Assigned",
          bgcolor: "#F79E1B40",
          color: "#F79E1B",
        };
      case "onstream":
        return {
          label: "On Stream",
          bgcolor: statusColors.fulfilled.bg,
          color: statusColors.fulfilled.fg,
        };
      default:
        return {
          label: "Unknown",
          bgcolor: statusColors.pending.bg,
          color: statusColors.pending.fg,
        };
    }
  };

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          {/* Crew Statistics */}
          <CrewStatistics crewStats={crewStats} />

          {/* Header Actions */}
          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                disabled
                sx={{
                  color: colors.primary[100],
                  borderColor: colors.primary[100],
                  borderWidth: 2,
                }}
              >
                Export Report
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/crew-management/rates')}
                sx={{
                  color: colors.primary[100],
                  borderColor: colors.primary[100],
                  borderWidth: 2,
                  '&:hover': {
                    color: colors.primary[400],
                    borderColor: colors.primary[400]
                  }
                }}
              >
                Rate Cards
              </Button>
              <Button
                variant="contained"
                startIcon={<AddCircleOutline />}
                sx={{ backgroundColor: colors.primary[400] }}
                onClick={() => setSearchMemberOpen(true)}
              >
                Add Crew Member
              </Button>
            </Box>
          </Box>

          {/* Featured Crew Members List & Live Activity Widget */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, lg: 8 }}>
              <Card sx={{ boxShadow: 0, height: "100%" }}>
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      pb: 1,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    }}
                    mb={2}
                    flexWrap="wrap"
                    gap={2}
                  >
                    <Box display={"flex"} alignItems={"center"} gap={1}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor} 100%)`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 4px 8px rgba(114, 78, 65, 0.3)",
                        }}
                      >
                        <People sx={{ color: "white", fontSize: 20 }} />
                      </Box>
                      <Box display={"flex"} alignItems={"center"} gap={1}>
                        <Box sx={{ lineHeight: 1 }}>
                          <Typography variant={"h6"} fontWeight={600}>
                            Crew List
                          </Typography>
                          <Typography
                            variant={"caption"}
                          >{`A featured crew members list.`}</Typography>
                        </Box>
                        {crewLoading && (
                          <CircularProgress color={"primary"} size={16} />
                        )}
                      </Box>
                    </Box>
                    <Chip
                      label="View All Crew"
                      variant="outlined"
                      size={"small"}
                      onClick={() => navigate("/crew-management/all-crew")}
                    />
                  </Box>
                  {crewError ? (
                    <Alert severity="error">{crewError}</Alert>
                  ) : crewWarning ? (
                    <Alert severity="warning">{crewWarning}</Alert>
                  ) : (
                    <Box
                      sx={{
                        maxHeight: "210px",
                        overflowY: "scroll",
                        scrollbarColor: "#888 transparent",
                        scrollbarWidth: "thin",
                      }}
                    >
                      {crew.map((member) => {
                        const availabilityChipProps =
                          getCrewAvailabilityChipProps(member.status);
                        return (
                          <Box
                            key={member.id}
                            onClick={() =>
                              navigate(`/crew-management/view/${member.id}`)
                            }
                            sx={{
                              border: "1px solid",
                              borderColor: "#A9B2C3",
                              "&:hover": {
                                borderColor: primaryHoverColor,
                              },
                              cursor: "pointer",
                              borderRadius: 2,
                              padding: "8px",
                              marginBottom: "8px",
                            }}
                          >
                            <Box
                              display={"flex"}
                              alignItems={"center"}
                              justifyContent={"space-between"}
                            >
                              <Box
                                display={"flex"}
                                alignItems={"center"}
                                gap={2}
                              >
                                <Avatar
                                  alt={`${member.name} ${member.surname}`}
                                  src={member.avatar}
                                />
                                <Box>
                                  <Typography variant="subtitle2">
                                    <strong>{`${member.name} ${member.surname}`}</strong>
                                  </Typography>
                                  <Typography variant="caption">
                                    {member.roles[0]?.name}
                                  </Typography>
                                </Box>
                              </Box>
                              <Chip
                                label={availabilityChipProps.label}
                                size={"small"}
                                sx={{
                                  color: availabilityChipProps.color,
                                  bgcolor: availabilityChipProps.bgcolor,
                                }}
                              />
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, lg: 4 }}>
              <Card sx={{ boxShadow: 0, height: "100%" }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      pb: 1,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Box display="flex" alignItems={"center"} gap={1}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor} 100%)`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 4px 8px rgba(114, 78, 65, 0.3)",
                        }}
                      >
                        <Stream sx={{ color: "white", fontSize: 24 }} />
                      </Box>
                      <Box sx={{ lineHeight: 1 }}>
                        <Typography variant={"h6"} fontWeight={600}>
                          Live Activity
                        </Typography>
                        <Typography
                          variant={"caption"}
                        >{`Real time pilot activity.`}</Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box>
                    {activities.map((activity) => (
                      <Grid
                        display={"flex"}
                        alignItems={"center"}
                        container
                        key={activity.id}
                        sx={{ px: 0, mb: "12px" }}
                      >
                        <Grid size={1.5}>
                          {getCrewActivityIcon(activity.type)}
                        </Grid>
                        <Grid size={10.5}>
                          <Typography variant="body2">
                            <strong>{activity.user}</strong>{" "}
                            {activity.message}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {activity.time}
                          </Typography>
                        </Grid>
                      </Grid>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Pending Assignments & Payments */}
          <Grid container sx={{ my: "16px" }} spacing={2}>
            <Grid size={{ xs: 12, lg: 6 }}>
              <Card sx={{ boxShadow: 0, height: "100%" }}>
                <CardContent>
                  <Box
                    display={"flex"}
                    mb={2}
                    sx={{
                      pb: 1,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    }}
                    alignItems={"center"}
                    gap={1}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor} 100%)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 4px 8px rgba(114, 78, 65, 0.3)",
                      }}
                    >
                      <WorkHistory sx={{ color: "white", fontSize: 20 }} />
                    </Box>
                    <Box sx={{ lineHeight: 1 }}>
                      <Typography variant={"h6"} fontWeight={600}>
                        Streams Pending Assignments
                      </Typography>
                      <Typography
                        variant={"caption"}
                      >{`All streams without callsheet assignments.`}</Typography>
                    </Box>
                    {streamsLoading && (
                      <CircularProgress color={"primary"} size={16} />
                    )}
                  </Box>

                  {streamsError ? (
                    <Alert severity="error">{streamsError}</Alert>
                  ) : streamsWarning ? (
                    <Alert severity="warning">{streamsWarning}</Alert>
                  ) : streams?.length === 0 ? (
                    <Chip
                      label={
                        <i>At the moment, all streams have callsheet assignments</i>
                      }
                      size="small"
                      variant="outlined"
                      sx={{
                        borderRadius: 1.5,
                        mb: 2,
                        borderColor: colors.primary[800],
                      }}
                    />
                  ) : (
                    <Box>
                      {streams.slice(0, 2).map((stream) => {
                        const priorityChipProps = getAssignmentPriorityChipProps(
                          isAfter(stream.serviceDate, new Date()) ? "high" : 'low'
                        );
                        return (
                          <Box
                            key={stream.id}
                            sx={{
                              border: "1px #CCC solid",
                              borderRadius: 2,
                              padding: "8px",
                              marginBottom: "8px",
                            }}
                          >
                            <Box
                              display={"flex"}
                              alignItems={"center"}
                              justifyContent={"space-between"}
                            >
                              <Box alignItems={"center"} gap={2}>
                                <Box
                                  display={"flex"}
                                  mb={1}
                                  alignItems={"center"}
                                  gap={1}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    onClick={() => navigate(`/stream-management/view/${stream.id}`)}
                                    sx={{
                                      cursor: 'pointer',
                                      "&:hover": {
                                        color: colors.primary[400]
                                      }
                                    }}>
                                    <strong>{stream.title}</strong>
                                  </Typography>
                                  <Chip
                                    label={stream.type}
                                    size={"small"}
                                    sx={{
                                      color: colors.primary[700],
                                      borderRadius: 1,
                                      fontWeight: 600,
                                      bgcolor: "#D6DDEB",
                                    }}
                                  />
                                </Box>

                                <Box>
                                  <Box
                                    mb={0.4}
                                    display={"flex"}
                                    alignItems={"center"}
                                    gap={"4px"}
                                  >
                                    <LocationIcon
                                      sx={{ fontSize: 18 }}
                                      color="disabled"
                                    />
                                    <Typography variant="body2">
                                      {stream.venue?.length
                                        ? stream.venue
                                        : "Location Not Provided"}
                                    </Typography>
                                  </Box>
                                  <Box
                                    display={"flex"}
                                    alignItems={"center"}
                                    gap={"4px"}
                                  >
                                    <CalendarIcon
                                      sx={{ fontSize: 18 }}
                                      color="disabled"
                                    />
                                    <Typography variant="body2">
                                      {stream.serviceDate}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>{" "}
                              <Chip
                                label={`${priorityChipProps.label} priority`}
                                size={"small"}
                                sx={{
                                  color: priorityChipProps.color,
                                  borderRadius: 1,
                                  bgcolor: priorityChipProps.bgcolor,
                                }}
                              />
                            </Box>
                            <Box
                              mt={1}
                              display={"flex"}
                              alignItems={"center"}
                              justifyContent={"space-between"}
                            >
                              <Box
                                display={"flex"}
                                alignItems={"center"}
                                gap={"4px"}
                              >
                                <People sx={{ fontSize: 18 }} />
                                <Typography variant="body2">{`4 crew members needed`}</Typography>
                              </Box>
                              <Box display={'flex'} alignItems={'center'} gap={1}>
                                <Button
                                  size={"small"}
                                  sx={{
                                    backgroundColor:
                                      colors.primary[800],
                                  }}
                                >
                                  Share Callsheet
                                </Button>
                                <Button
                                  size={"small"}
                                  onClick={() =>
                                    navigate(
                                      `/stream-management/view/${stream.id}/callsheet`
                                    )
                                  }
                                  variant="contained"
                                >
                                  Assign Crew
                                </Button>
                              </Box>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <Card sx={{ boxShadow: 0, height: "100%" }}>
                <CardContent>
                  <Box
                    display={"flex"}
                    mb={2}
                    sx={{
                      pb: 1,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    }}
                    alignItems={"center"}
                    gap={1}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor} 100%)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 4px 8px rgba(114, 78, 65, 0.3)",
                      }}
                    >
                      <Savings sx={{ color: "white", fontSize: 20 }} />
                    </Box>
                    <Box sx={{ lineHeight: 1 }}>
                      <Typography variant={"h6"} fontWeight={600}>
                        Wallet & Payments
                      </Typography>
                      <Typography
                        variant={"caption"}
                      >{`Pending payouts & withdrawal requests.`}</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Tabs
                      variant="fullWidth"
                      sx={{
                        bgcolor: colors.primary[800],
                        padding: 0.8,
                        mb: 1,
                        borderRadius: 2,
                        transition: "0.3s ease-in-out",
                      }}
                    >
                      <Tab
                        onClick={() => setActiveTab(0)}
                        label="Pending Payouts"
                        sx={{
                          textTransform: "capitalize",
                          mr: 1,
                          lineHeight: 1,
                          my: -0.8,
                          bgcolor: activeTab === 0 ? colors.primary[900] : "transparent",
                          borderRadius: 1.5,
                        }}
                      />
                      <Tab
                        onClick={() => setActiveTab(1)}
                        label="Withdrawal Requests"
                        sx={{
                          textTransform: "capitalize",
                          ml: 1,
                          lineHeight: 1,
                          my: -0.8,
                          bgcolor: activeTab === 1 ? colors.primary[900] : "transparent",
                          borderRadius: 1.5,
                        }}
                      />
                    </Tabs>
                    {activeTab === 0 && (
                      <Box>
                        {crewPayments.map((payment) => {
                          return (
                            <Box
                              key={payment.id}
                              mb={"8px"}
                              display={"flex"}
                              alignItems={"center"}
                              justifyContent={"space-between"}
                            >
                              <Box display="flex" alignItems="center" gap={2}>
                                <Box
                                  sx={{
                                    backgroundColor: `#EB433540`,
                                    borderRadius: "25%",
                                    p: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <AttachMoney
                                    sx={{ fontSize: 24 }}
                                    color={"error"}
                                  />
                                </Box>
                                <Box sx={{ lineHeight: 1 }}>
                                  <Typography variant="subtitle2">
                                    <strong>{payment.user}</strong>
                                  </Typography>
                                  <Typography variant="caption">
                                    {payment.date}
                                  </Typography>
                                </Box>
                              </Box>
                              <Typography
                                variant="h5"
                                fontWeight={600}
                              >{`$${payment.amount}`}</Typography>
                            </Box>
                          );
                        })}
                        <Button
                          sx={{
                            mt: "4px",
                            width: "100%",
                            textTransform: "capitalize",
                          }}
                          variant="contained"
                        >
                          Process Payments
                        </Button>
                      </Box>
                    )}
                    {activeTab === 1 && <Box>Withdrawal Requests</Box>}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Feedback & Ratings */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid size={{ xs: 12, lg: 6 }}>
              <Card sx={{ boxShadow: 0, height: "100%" }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      pb: 1,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor} 100%)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 4px 8px rgba(114, 78, 65, 0.3)",
                      }}
                    >
                      <Star sx={{ color: "white", fontSize: 20 }} />
                    </Box>
                    <Box sx={{ lineHeight: 1 }}>
                      <Typography variant={"h6"} fontWeight={600}>
                        Feedback & Ratings
                      </Typography>
                      <Typography
                        variant={"caption"}
                      >{`Recent excellent performers & what was said about them.`}</Typography>
                    </Box>
                  </Box>

                  <Box mb={1}>
                    <Typography variant="h6">Top Rated This Month</Typography>
                  </Box>
                  {topRatedCrew ? (topRatedCrew.map(trc => {
                    return (
                      <Box
                        key={trc.id}
                        display="flex"
                        sx={{
                          mb: 1,
                          padding: 0.8,
                          borderRadius: 2,
                          bgcolor: colors.primary[800],
                        }}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                      >
                        <TopRatedPilotDetail plt={trc.crew_member} />
                        <Rating
                          size="small"
                          value={trc.rating}
                          precision={0.1}
                          readOnly
                        />
                      </Box>
                    );
                  })) : <Chip size={"small"} sx={{ borderRadius: 1 }} label={"Not Available At The Moment"} />}

                  <Box mt={4} mb={1}>
                    <Typography variant="h6">Most Recent Feedback</Typography>
                  </Box>
                  {mostRecentFeedback ? (
                    <Box
                      sx={{
                        border: "1px #CCC solid",
                        borderRadius: 2,
                        padding: "8px",
                        mt: "8px",
                      }}
                    >
                      <Box
                        display={"flex"}
                        sx={{
                          borderBottom: "1px solid",
                          pb: 1,
                          borderColor: "divider",
                        }}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                      >
                        <TopRatedPilotDetail
                          plt={mostRecentFeedback.crew_member}
                        />
                        <Rating
                          size="small"
                          value={mostRecentFeedback.rating}
                          precision={0.1}
                          readOnly
                        />
                      </Box>
                      <Box mt={1}>
                        <Typography variant="body2">
                          {mostRecentFeedback.comment}
                        </Typography>
                        <MostRecentRater usr={mostRecentFeedback.rater} />
                      </Box>
                    </Box>
                  ) : (
                    <Chip
                      size={"small"}
                      sx={{ borderRadius: 1 }}
                      label={"Not Available At The Moment"}
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Dialogs */}

      <SearchPilotDialog
        searchMemberOpen={searchMemberOpen}
        setSearchMemberOpen={setSearchMemberOpen}
        searchResults={searchResults}
        setSearchResults={setSearchResults}
        setAddMemberOpen={setAddMemberOpen}
      />

      <AddCrewMemberDialog
        addMemberOpen={addMemberOpen}
        formData={formData}
        user={searchResults}
        formLoading={formLoading}
        handleAddCrewMember={handleAddCrewMember}
        handleEmergencyContactChange={handleEmergencyContactChange}
        handleInputChange={handleInputChange}
        handlePhoneChange={handlePhoneChange}
        handleSelectChange={handleSelectChange}
        onCountryChange={handleCountryChange}
        onRolesChange={handleRolesChange}
        onSkillsChange={handleSkillsChange}
        onToolsChange={handleToolsChange}
        removeRole={removeRole}
        removeSkill={removeSkill}
        removeTool={removeTool}
        resetForm={resetForm}
        selectedCountry={selectedCountry}
        selectedRoles={selectedRoles}
        selectedSkills={selectedSkills}
        selectedTools={selectedTools}
        setAddMemberOpen={setAddMemberOpen}
        setFormData={setFormData}
      />
    </Box>
  );
};

export default CrewManagement;

interface TopRatedPilotDetailProps {
  plt: number;
}

const TopRatedPilotDetail: React.FC<TopRatedPilotDetailProps> = ({ plt }) => {
  const [ratedPilot, setRatedPilot] = useState<{
    name: string;
    avatar: string | null;
  } | null>();
  const [ratedPilotLoading, setRatedPilotLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchRatedPilot = async () => {
      setRatedPilotLoading(true);
      try {
        const res = await apiService.get<PilotAPIResponse>(
          `/api/crews/crew-members/${plt}/`
        );
        setRatedPilot({
          name: `${res.data.user.first_name} ${res.data.user.last_name}`,
          avatar: res.data.user.profile_picture,
        });
      } catch (err) {
        console.error("Failed to fetch pilot details:", err);
      } finally {
        setRatedPilotLoading(false);
      }
    };

    fetchRatedPilot();
  }, [plt]);

  if (ratedPilotLoading) {
    return <CircularProgress size={20} sx={{ color: "primary" }} />;
  }
  return (
    <Box sx={{ lineHeight: 1 }}>
      <Box
        display="flex"
        alignItems={"center"}
        justifyContent={"center"}
        gap={1}
      >
        <Avatar src={ratedPilot?.avatar || ""} sx={{ width: 32, height: 32 }} />
        <Box sx={{ lineHeight: 1 }}>
          <Typography variant="body2" fontWeight={600} color="text.primary">
            {ratedPilot?.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {`ID: ${plt}`}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

interface MostRecentRaterProps {
  usr: number;
}

const MostRecentRater: React.FC<MostRecentRaterProps> = ({ usr }) => {
  const [recentRater, setRecentRater] = useState<{
    name: string;
    role: string | null;
  } | null>();
  const [recentRaterLoading, setRecentRaterLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchRecentRater = async () => {
      setRecentRaterLoading(true);
      try {
        const res = await apiService.get<UserProps>(
          `/api/authentication/users/${usr}/`
        );
        setRecentRater({
          name: res.data.first_name.length === 0 ? 'Unnamed Rater' :  `${res.data.first_name} ${res.data.last_name}`,
          role: res.data.role.length === 0 ? 'Unspecified Role' : res.data.role
        });
      } catch (err) {
        console.error("Failed to fetch pilot details:", err);
      } finally {
        setRecentRaterLoading(false);
      }
    };

    fetchRecentRater();
  }, [usr]);

  if (recentRaterLoading) {
    return <CircularProgress size={20} sx={{ color: "primary" }} />;
  }

  return (
    <Box mt={0.4}>
      {!recentRater ? (
        <Chip
          size={"small"}
          sx={{ borderRadius: 1, textTransform: "uppercase" }}
          label={<i>Rater Not Specified</i>}
        />
      ) : (
        <Chip
          size={"small"}
          sx={{ borderRadius: 1, textTransform: "uppercase" }}
          label={`${recentRater?.name} - ${recentRater?.role}`}
        />
      )}
    </Box>
  );
};
